import { useEffect, useRef } from 'react';

// Shared requestAnimationFrame loop for the animated viz widgets. Calls `cb`
// with the delta (seconds) and absolute elapsed time each frame, and AUTOMATICALLY
// PAUSES when the tab is hidden (visibilitychange) so background slides don't
// burn CPU. `active` lets a widget stop the loop (e.g. a paused control).
export function useAnimationFrame(
  cb: (dt: number, elapsed: number) => void,
  active = true,
) {
  const cbRef = useRef(cb);
  cbRef.current = cb;

  useEffect(() => {
    if (!active) return;
    let raf = 0;
    let last = performance.now();
    let elapsed = 0;
    let hidden = document.hidden;

    const onVisibility = () => {
      hidden = document.hidden;
      // Reset the clock on resume so we don't apply one giant dt jump.
      if (!hidden) last = performance.now();
    };
    document.addEventListener('visibilitychange', onVisibility);

    const loop = (now: number) => {
      raf = requestAnimationFrame(loop);
      if (hidden) {
        last = now;
        return;
      }
      const dt = Math.min(0.05, (now - last) / 1000); // clamp to avoid jumps
      last = now;
      elapsed += dt;
      cbRef.current(dt, elapsed);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [active]);
}
